import React, { useEffect, useRef } from 'react';

const ConnectDotsBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    let animationFrameId;
    let particles = [];
    let currentNumParticles = window.innerWidth < 768 ? 30 : 80;
    let width = window.innerWidth;
    let height = window.innerHeight;

    // Mouse interaction
    let mouse = {
      x: null,
      y: null,
      radius: 150
    };

    const handleMouseMove = (event) => {
      if (canvas) {
        const rect = canvas.getBoundingClientRect();
        mouse.x = event.clientX - rect.left;
        mouse.y = event.clientY - rect.top;
      }
    };

    const handleMouseOut = () => {
      mouse.x = null;
      mouse.y = null;
    };

    const handleResize = () => {
      const newWidth = window.innerWidth;
      if (Math.abs(newWidth - width) > 100) {
        setTimeout(init, 100);
      }
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseout', handleMouseOut);
    window.addEventListener('resize', handleResize);

    canvas.width = width;
    canvas.height = height;

    class Particle {
      constructor() {
        this.x = Math.random() * width;
        this.y = Math.random() * height;
        this.size = Math.random() * 4 + 2; // 2 to 6
        this.baseX = this.x;
        this.baseY = this.y;
        this.density = (Math.random() * 30) + 1;
        this.velocityX = (Math.random() - 0.5) * 0.5;
        this.velocityY = (Math.random() - 0.5) * 0.5;
      }

      draw(isDark) {
        // Light mode: light blue dots; Dark mode: gold dots
        ctx.fillStyle = isDark ? 'rgba(255, 215, 0, 0.8)' : 'rgba(64, 224, 208, 0.8)';
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.closePath();
        ctx.fill();
      }

      update() {
        // Move naturally
        this.x += this.velocityX;
        this.y += this.velocityY;

        // Bounce off edges
        if (this.x < 0 || this.x > width) this.velocityX = -this.velocityX;
        if (this.y < 0 || this.y > height) this.velocityY = -this.velocityY;

        // Mouse interaction: push away slightly
        if (mouse.x != null && mouse.y != null) {
          let dx = mouse.x - this.x;
          let dy = mouse.y - this.y;
          let distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < mouse.radius) {
            const forceDirectionX = dx / distance;
            const forceDirectionY = dy / distance;
            const maxDistance = mouse.radius;
            const force = (maxDistance - distance) / maxDistance;
            const directionX = forceDirectionX * force * this.density * 0.02;
            const directionY = forceDirectionY * force * this.density * 0.02;

            this.x -= directionX;
            this.y -= directionY;
          }
        }
      }
    }

    const init = () => {
      particles = [];
      currentNumParticles = window.innerWidth < 768 ? 30 : 80;
      for (let i = 0; i < currentNumParticles; i++) {
        particles.push(new Particle());
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, width, height);
      const isDark = document.documentElement.classList.contains('dark');

      for (let i = 0; i < particles.length; i++) {
        particles[i].update();
        particles[i].draw(isDark);

        // Connect dots
        for (let j = i; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          // Only connect if distance is small
          const maxDistance = window.innerWidth < 768 ? 100 : 200;
          if (distance < maxDistance) {
            ctx.beginPath();
            const opacity = 0.8 - (distance / maxDistance) * 0.8;
            // Light mode: lighter grey dashed line; Dark mode: light blue dashed line
            ctx.strokeStyle = isDark
              ? `rgba(64, 224, 208, ${opacity})`
              : `rgba(150, 160, 170, ${opacity})`;
            ctx.setLineDash([5, 5]); // dashed line
            ctx.lineWidth = 1;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
            ctx.setLineDash([]); // reset for next drawing operations
          }
        }
      }
      animationFrameId = requestAnimationFrame(animate);
    };

    init();
    animate();

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseout', handleMouseOut);
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute top-0 left-0 w-full h-full z-0 pointer-events-none"
    />
  );
};

export default ConnectDotsBackground;
